/**
 * Sanction Snowboard Buyer's Guide
 * =================================
 * Standalone JS for the buyer's guide landing page.
 * - Size calculator (adapted from product-size-finder logic)
 * - Sticky chapter nav with active section highlighting
 * - Smooth anchor scrolling with offset
 * - Accordion expand/collapse
 * - IntersectionObserver scroll animations
 */

(function () {
  'use strict';

  // ==========================================
  // HERO PARALLAX — Scroll-Driven Animation
  // ==========================================
  function initHeroParallax() {
    var hero = document.querySelector('.sbg-hero');
    if (!hero) return;

    // Respect reduced motion preference
    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    var mountains = hero.querySelectorAll('.sbg-hero__mountain');
    var crystals = hero.querySelectorAll('.sbg-hero__crystal');
    var glow = hero.querySelector('.sbg-hero__glow');
    var content = hero.querySelector('.sbg-hero__content');
    var heroHeight = hero.offsetHeight;
    var ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(function () {
        var scrollY = window.pageYOffset || document.documentElement.scrollTop;

        // Only animate while hero is in view (plus a small buffer)
        if (scrollY > heroHeight * 1.2) {
          ticking = false;
          return;
        }

        var progress = Math.min(scrollY / heroHeight, 1); // 0 → 1 as hero scrolls out

        // Mountains: translate down at different speeds (parallax depth)
        for (var i = 0; i < mountains.length; i++) {
          var speed = parseFloat(mountains[i].getAttribute('data-parallax-speed')) || 0.2;
          var yOffset = scrollY * speed;
          mountains[i].style.transform = 'translateY(' + yOffset + 'px)';
        }

        // Crystals: translate + rotate on scroll for a drifting effect
        for (var j = 0; j < crystals.length; j++) {
          var cSpeed = parseFloat(crystals[j].getAttribute('data-parallax-speed')) || 0.2;
          var floatOffset = parseFloat(crystals[j].getAttribute('data-float-offset')) || 0;
          var cY = scrollY * cSpeed;
          // Gentle horizontal drift using sin wave
          var cX = Math.sin((scrollY * 0.003) + floatOffset) * 15;
          // Slow rotation tied to scroll
          var rotation = scrollY * (0.02 + (floatOffset * 0.008));
          crystals[j].style.transform = 'translate(' + cX + 'px, ' + cY + 'px) rotate(' + rotation + 'deg)';
        }

        // Glow: subtle vertical shift + scale
        if (glow) {
          var gSpeed = parseFloat(glow.getAttribute('data-parallax-speed')) || 0.08;
          var gY = scrollY * gSpeed;
          var gScale = 1 + (progress * 0.15);
          glow.style.transform = 'translateX(-50%) translateY(' + gY + 'px) scale(' + gScale + ')';
        }

        // Hero content: scroll up faster than page (text lifts away)
        if (content) {
          var contentY = scrollY * 0.4;
          var contentOpacity = 1 - (progress * 1.5); // fades out as you scroll
          contentOpacity = Math.max(contentOpacity, 0);
          content.style.transform = 'translateY(-' + contentY + 'px)';
          content.style.opacity = contentOpacity;
        }

        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      heroHeight = hero.offsetHeight;
    }, { passive: true });

    // Run once at init to handle page-refresh mid-scroll
    onScroll();
  }

  // ==========================================
  // SCROLL REVEAL ANIMATIONS
  // ==========================================
  function initScrollReveal() {
    var reveals = document.querySelectorAll('.sbg-reveal');
    if (!reveals.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('sbg-reveal--visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -40px 0px'
    });

    reveals.forEach(function (el) {
      observer.observe(el);
    });
  }

  // ==========================================
  // STICKY NAV
  // ==========================================
  function initStickyNav() {
    var nav = document.getElementById('sbg-nav');
    if (!nav) return;

    var track = nav.querySelector('.sbg-nav__track');
    var trackWrap = nav.querySelector('.sbg-nav__track-wrap');
    var links = nav.querySelectorAll('.sbg-nav__link');
    var sections = [];

    links.forEach(function (link) {
      var sectionId = link.getAttribute('data-section');
      var section = document.getElementById(sectionId);
      if (section) {
        sections.push({ id: sectionId, el: section, link: link });
      }
    });

    // Detect theme header height for sticky offset
    function getHeaderHeight() {
      // Measure actual header element first (most reliable on mobile)
      var header = document.querySelector('.section-header') || document.querySelector('.pageheader');
      if (header) {
        var rect = header.getBoundingClientRect();
        // Use rect.bottom instead of rect.height — when the theme hides the header
        // on scroll-down (e.g. transform: translateY(-100%)), rect.bottom drops to 0
        // so the sticky nav correctly moves to the top of the viewport.
        if (rect.bottom > 0) return rect.bottom;
        return 0;
      }
      // Fallback to CSS variable
      var style = getComputedStyle(document.documentElement);
      var h = parseInt(style.getPropertyValue('--theme-header-height')) || 0;
      return h;
    }

    var lastHeaderH = 0;
    function setStickyTop() {
      var headerH = getHeaderHeight();
      if (headerH !== lastHeaderH) {
        nav.style.top = headerH + 'px';
        lastHeaderH = headerH;
      }
    }
    setStickyTop();
    window.addEventListener('resize', setStickyTop, { passive: true });
    window.addEventListener('scroll', setStickyTop, { passive: true });

    // Mobile scroll-fade indicators
    function updateFadeHints() {
      if (!track || !trackWrap) return;
      var scrollLeft = track.scrollLeft;
      var maxScroll = track.scrollWidth - track.clientWidth;

      if (scrollLeft > 8) {
        trackWrap.classList.add('sbg-nav__track-wrap--fade-left');
      } else {
        trackWrap.classList.remove('sbg-nav__track-wrap--fade-left');
      }

      if (scrollLeft < maxScroll - 8) {
        trackWrap.classList.add('sbg-nav__track-wrap--fade-right');
      } else {
        trackWrap.classList.remove('sbg-nav__track-wrap--fade-right');
      }
    }

    if (track) {
      track.addEventListener('scroll', updateFadeHints, { passive: true });
      // Initial check after layout
      setTimeout(updateFadeHints, 100);
    }

    // Scroll shadow
    function updateNavShadow() {
      var scrollY = window.pageYOffset || document.documentElement.scrollTop;
      if (scrollY > 100) {
        nav.classList.add('sbg-nav--scrolled');
      } else {
        nav.classList.remove('sbg-nav--scrolled');
      }
    }

    // Active section highlighting
    function updateActiveSection() {
      var headerH = getHeaderHeight();
      var totalNavH = headerH + nav.offsetHeight + 20;
      var current = null;

      for (var i = sections.length - 1; i >= 0; i--) {
        var rect = sections[i].el.getBoundingClientRect();
        if (rect.top <= totalNavH + 40) {
          current = sections[i];
          break;
        }
      }

      if (current) {
        links.forEach(function (l) { l.classList.remove('sbg-nav__link--active'); });
        current.link.classList.add('sbg-nav__link--active');

        // Smooth scroll active link into view on mobile
        if (track) {
          var linkRect = current.link.getBoundingClientRect();
          var trackRect = track.getBoundingClientRect();
          if (linkRect.left < trackRect.left + 32 || linkRect.right > trackRect.right - 32) {
            var linkCenter = current.link.offsetLeft - (track.clientWidth / 2) + (current.link.offsetWidth / 2);
            track.scrollTo({ left: linkCenter, behavior: 'smooth' });
          }
        }
      }
    }

    var scrollTimer;
    window.addEventListener('scroll', function () {
      updateNavShadow();
      if (scrollTimer) cancelAnimationFrame(scrollTimer);
      scrollTimer = requestAnimationFrame(updateActiveSection);
    }, { passive: true });

    updateNavShadow();
    updateActiveSection();
  }

  // ==========================================
  // SMOOTH ANCHOR SCROLLING
  // ==========================================
  // Native anchor scroll via CSS scroll-behavior: smooth + scroll-margin-top
  // Handled in template inline script and CSS — no JS needed
  function initSmoothScroll() {}

  // ==========================================
  // ACCORDION
  // ==========================================
  function initAccordions() {
    var triggers = document.querySelectorAll('.sbg-accordion__trigger');

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var item = trigger.closest('.sbg-accordion__item');
        var body = item.querySelector('.sbg-accordion__body');
        var isOpen = item.classList.contains('sbg-accordion__item--open');

        // Close all others
        var allItems = trigger.closest('.sbg-accordion').querySelectorAll('.sbg-accordion__item');
        allItems.forEach(function (other) {
          if (other !== item) {
            other.classList.remove('sbg-accordion__item--open');
            var otherBody = other.querySelector('.sbg-accordion__body');
            if (otherBody) otherBody.style.maxHeight = null;
            var otherTrigger = other.querySelector('.sbg-accordion__trigger');
            if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
          }
        });

        // Toggle current
        if (isOpen) {
          item.classList.remove('sbg-accordion__item--open');
          body.style.maxHeight = null;
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('sbg-accordion__item--open');
          body.style.maxHeight = body.scrollHeight + 'px';
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // ==========================================
  // SIZE CALCULATOR
  // ==========================================
  function initSizeCalculator() {
    var calcBtn = document.getElementById('sbg-calc-btn');
    if (!calcBtn) return;

    var feetInput = document.getElementById('sbg-feet');
    var inchesInput = document.getElementById('sbg-inches');
    var weightInput = document.getElementById('sbg-weight');
    var bootInput = document.getElementById('sbg-boot');
    var styleSelect = document.getElementById('sbg-style');
    var resultEl = document.getElementById('sbg-calc-result');
    var sizeValueEl = document.getElementById('sbg-result-size');
    var noteEl = document.getElementById('sbg-result-note');
    var widthEl = document.getElementById('sbg-result-width');
    var widthNoteEl = document.getElementById('sbg-result-width-note');
    var widthCard = document.getElementById('sbg-result-width-card');

    // Size chart: [minWeight, maxWeight, heightInches, minSize, maxSize]
    var sizeChart = [
      [100, 120, 58, 138, 146],
      [110, 130, 60, 142, 150],
      [120, 140, 62, 146, 154],
      [130, 150, 64, 149, 156],
      [140, 160, 66, 151, 158],
      [150, 170, 68, 153, 160],
      [160, 185, 70, 155, 162],
      [170, 195, 72, 156, 164],
      [180, 210, 74, 157, 166],
      [210, 400, 76, 159, 168]
    ];

    function clearErrors() {
      [feetInput, weightInput, bootInput].forEach(function (input) {
        input.classList.remove('sbg-calculator__input--error');
      });
    }

    function calculate() {
      clearErrors();

      var feet = parseInt(feetInput.value) || 0;
      var inches = parseInt(inchesInput.value) || 0;
      var weight = parseInt(weightInput.value) || 0;
      var boot = parseFloat(bootInput.value) || 0;
      var style = styleSelect.value;

      var hasError = false;
      if (!feet || feet < 3 || feet > 7) {
        feetInput.classList.add('sbg-calculator__input--error');
        hasError = true;
      }
      if (!weight || weight < 80 || weight > 400) {
        weightInput.classList.add('sbg-calculator__input--error');
        hasError = true;
      }
      if (!boot || boot < 4 || boot > 16) {
        bootInput.classList.add('sbg-calculator__input--error');
        hasError = true;
      }
      if (hasError) return;

      var totalInches = (feet * 12) + inches;

      // Find best match by weight
      var bestMatch = null;
      var minDiff = Infinity;

      for (var i = 0; i < sizeChart.length; i++) {
        var row = sizeChart[i];
        var midWeight = (row[0] + row[1]) / 2;
        var diff = Math.abs(weight - midWeight);

        if (weight >= row[0] - 10 && weight <= row[1] + 10 && diff < minDiff) {
          minDiff = diff;
          bestMatch = { minW: row[0], maxW: row[1], heightIn: row[2], minSize: row[3], maxSize: row[4] };
        }
      }

      if (!bestMatch) {
        bestMatch = weight < 100
          ? { minW: 100, maxW: 120, heightIn: 58, minSize: 132, maxSize: 148 }
          : { minW: 210, maxW: 400, heightIn: 76, minSize: 158, maxSize: 168 };
      }

      // Calculate size range
      var weightRatio = Math.min(1, Math.max(0, (weight - bestMatch.minW) / (bestMatch.maxW - bestMatch.minW)));
      var rangeSize = bestMatch.maxSize - bestMatch.minSize;
      var centerPoint = bestMatch.minSize + (rangeSize * (weightRatio * 0.35));

      // Height adjustment
      var heightDiff = totalInches - bestMatch.heightIn;
      var heightAdjust = 0;
      if (heightDiff >= 2) heightAdjust = Math.min(2, Math.floor(heightDiff / 2));
      else if (heightDiff <= -2) heightAdjust = Math.max(-2, Math.ceil(heightDiff / 2));

      // Style adjustment
      var styleAdjust = 0;
      if (style === 'freestyle') styleAdjust = -2;
      else if (style === 'freeride') styleAdjust = 2;

      var adjustedCenter = centerPoint + heightAdjust + styleAdjust;
      var sizeMin = Math.max(bestMatch.minSize, Math.round(adjustedCenter - 3));
      var sizeMax = Math.min(bestMatch.maxSize, Math.round(adjustedCenter + 3));

      // Width
      var needsWide = boot >= 11;

      // Display results
      if (sizeMin === sizeMax) {
        sizeValueEl.textContent = sizeMin;
      } else {
        sizeValueEl.textContent = sizeMin + '\u2013' + sizeMax;
      }

      // Note
      var note = '';
      if (style === 'freestyle') note = 'Sized slightly shorter for park and freestyle maneuverability.';
      else if (style === 'freeride') note = 'Sized slightly longer for freeride stability and powder float.';
      else note = 'A versatile all-mountain size range for your weight and height.';
      noteEl.textContent = note;

      // Width
      widthEl.textContent = needsWide ? 'Wide' : 'Regular';
      widthCard.classList.remove('sbg-result-card--wide');
      if (needsWide) {
        widthCard.classList.add('sbg-result-card--wide');
        widthNoteEl.textContent = 'With boot size ' + boot + ', you need a wide board (255mm+ waist) to prevent toe/heel drag.';
      } else {
        widthNoteEl.textContent = 'Your boot size fits standard width boards comfortably.';
      }

      resultEl.classList.add('sbg-calculator__result--visible');

      // Scroll result into view on mobile
      if (window.innerWidth < 768) {
        setTimeout(function () {
          resultEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }
    }

    calcBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      calculate();
    });

    // Also listen via event delegation on the calculator container
    var calcContainer = document.getElementById('sbg-size-calc');
    if (calcContainer) {
      calcContainer.addEventListener('click', function (e) {
        if (e.target.closest('.sbg-calculator__btn')) {
          e.preventDefault();
          e.stopPropagation();
          calculate();
        }
      });
    }

    // Enter key support
    [feetInput, inchesInput, weightInput, bootInput].forEach(function (input) {
      input.addEventListener('keypress', function (e) {
        if (e.key === 'Enter') calculate();
      });
      input.addEventListener('input', function () {
        input.classList.remove('sbg-calculator__input--error');
      });
    });
  }

  // ==========================================
  // PRODUCT CAROUSELS (Owl Carousel)
  // ==========================================
  function initProductCarousels() {
    function startCarousels() {
      if (typeof jQuery === 'undefined' || typeof jQuery.fn.owlCarousel === 'undefined') {
        console.warn('[SBG] jQuery or Owl Carousel not loaded');
        return;
      }

      var $ = jQuery;
      var carouselOpts = {
        nav: true,
        dots: false,
        margin: 16,
        loop: false,
        responsive: {
          0: { items: 1, stagePadding: 40 },
          480: { items: 2, stagePadding: 0 },
          768: { items: 3 },
          1100: { items: 4 }
        },
        onInitialized: function () {
          $(this.$element).find('.owl-stage').css({ 'margin-left': '0', 'padding-left': '0' });
        },
        onResized: function () {
          $(this.$element).find('.owl-stage').css({ 'margin-left': '0', 'padding-left': '0' });
        }
      };

      $('.sbg-picks-carousel').each(function () {
        var $carousel = $(this);
        if ($carousel.find('.sbg-picks__item').length > 0) {
          $carousel.owlCarousel(carouselOpts);
        }
      });
    }

    // Owl Carousel may not be ready immediately — retry with delay
    if (typeof jQuery !== 'undefined' && typeof jQuery.fn.owlCarousel !== 'undefined') {
      startCarousels();
    } else {
      var attempts = 0;
      var retryInterval = setInterval(function () {
        attempts++;
        if (typeof jQuery !== 'undefined' && typeof jQuery.fn.owlCarousel !== 'undefined') {
          clearInterval(retryInterval);
          startCarousels();
        } else if (attempts >= 20) {
          clearInterval(retryInterval);
          console.warn('[SBG] Owl Carousel not available after retries');
        }
      }, 200);
    }

    // Staff icon image fallback (png → jpg → jpeg)
    document.querySelectorAll('.sbg-picks__staff-img').forEach(function (img) {
      img.addEventListener('error', function () {
        var jpgUrl = img.getAttribute('data-staff-jpg');
        var jpegUrl = img.getAttribute('data-staff-jpeg');
        if (img.src.indexOf('.png') !== -1 && jpgUrl) {
          img.src = jpgUrl;
        } else if (img.src.indexOf('.jpg') !== -1 && jpegUrl) {
          img.src = jpegUrl;
        }
      });
    });
  }

  // ==========================================
  // FIND YOUR STYLE QUIZ
  // ==========================================
  function initStyleQuiz() {
    var quiz = document.getElementById('sbg-style-quiz');
    if (!quiz) return;

    var steps = quiz.querySelectorAll('.sbg-quiz__step');
    var progressFill = document.getElementById('sbg-quiz-progress');
    var stepText = document.getElementById('sbg-quiz-step-text');
    var resultEl = document.getElementById('sbg-quiz-result');
    var retakeBtn = document.getElementById('sbg-quiz-retake');
    var progressBar = quiz.querySelector('.sbg-quiz__progress');

    var answers = {};
    var currentStep = 1;
    var totalSteps = 3;

    var results = {
      freestyle: {
        title: 'Freestyle',
        desc: 'You\'re all about expression and creativity. Look for a softer flex, true twin shape board that\'s sized slightly shorter for quick spins and easy presses. Camber or hybrid camber profiles give you pop for ollies and stability on features.',
        traits: ['Soft-Medium Flex', 'True Twin Shape', 'Shorter Length', 'Park & Jib Ready'],
        url: '/collections/snowboards?filter.v.availability=1&filter.p.m.specs.terrain=Freestyle&sort_by=best-selling'
      },
      'all-mountain': {
        title: 'All-Mountain',
        desc: 'You want one board that handles everything \u2014 groomers, powder stashes, and the occasional park lap. A medium flex, directional twin shape with hybrid camber gives you the best blend of versatility, edge hold, and float.',
        traits: ['Medium Flex', 'Directional Twin', 'Hybrid Camber', 'Do-It-All'],
        url: '/collections/snowboards?filter.v.availability=1&filter.p.m.specs.terrain=All-Mountain&sort_by=best-selling'
      },
      freeride: {
        title: 'Freeride',
        desc: 'You chase powder, steep lines, and untracked terrain. A stiffer flex, directional shape board sized slightly longer gives you the float, stability, and edge hold you need when the mountain gets serious.',
        traits: ['Stiff Flex', 'Directional Shape', 'Longer Length', 'Powder & Steeps'],
        url: '/collections/snowboards?filter.v.availability=1&filter.p.m.specs.terrain=Freeride&sort_by=best-selling'
      },
      'beginner-am': {
        title: 'Beginner All-Mountain',
        desc: 'You\'re building your skills and want a board that\'s forgiving and confidence-inspiring. A softer flex, rocker or hybrid profile board will be easy to turn, catch fewer edges, and help you progress faster.',
        traits: ['Soft Flex', 'Rocker Profile', 'Forgiving', 'Easy to Progress'],
        url: '/collections/snowboards?filter.v.availability=1&filter.p.m.specs.terrain=All-Mountain&filter.p.m.specs.ability_level=Beginner-Intermediate&sort_by=best-selling'
      }
    };

    function getResult() {
      var terrain = answers['1'];
      var level = answers['2'];
      var priority = answers['3'];

      // Beginner path
      if (level === 'beginner') return 'beginner-am';

      // Park / freestyle signals
      if (terrain === 'park' || priority === 'playful') {
        if (priority === 'powerful') return 'all-mountain';
        return 'freestyle';
      }

      // Powder / freeride signals
      if (terrain === 'powder' || priority === 'powerful') {
        if (level === 'advanced') return 'freeride';
        return 'all-mountain';
      }

      // Groomers
      if (terrain === 'groomers') {
        if (priority === 'versatile') return 'all-mountain';
        if (level === 'advanced') return 'freeride';
        return 'all-mountain';
      }

      // Default: all-mountain
      return 'all-mountain';
    }

    function showStep(step) {
      steps.forEach(function (s) { s.classList.remove('sbg-quiz__step--active'); });
      var target = quiz.querySelector('[data-step="' + step + '"]');
      if (target) target.classList.add('sbg-quiz__step--active');
      progressFill.style.width = ((step / totalSteps) * 100) + '%';
      stepText.textContent = step + ' of ' + totalSteps;
    }

    function showResult() {
      var key = getResult();
      var data = results[key];

      document.getElementById('sbg-quiz-result-title').textContent = data.title;
      document.getElementById('sbg-quiz-result-desc').textContent = data.desc;

      var traitsEl = document.getElementById('sbg-quiz-result-traits');
      traitsEl.innerHTML = '';
      data.traits.forEach(function (t) {
        var span = document.createElement('span');
        span.className = 'sbg-quiz__result-trait';
        span.textContent = t;
        traitsEl.appendChild(span);
      });

      var cta = document.getElementById('sbg-quiz-result-cta');
      cta.href = data.url;
      var ctaText = document.getElementById('sbg-quiz-result-cta-text');
      if (ctaText) ctaText.textContent = 'Shop ' + data.title + ' Boards';

      // Hide steps and progress, show result
      steps.forEach(function (s) { s.classList.remove('sbg-quiz__step--active'); });
      progressBar.style.display = 'none';
      resultEl.classList.add('sbg-quiz__result--visible');
    }

    // Card click handler
    quiz.addEventListener('click', function (e) {
      var card = e.target.closest('.sbg-quiz__card');
      if (!card) return;

      var step = card.closest('.sbg-quiz__step');
      if (!step) return;

      var stepNum = parseInt(step.getAttribute('data-step'));
      var value = card.getAttribute('data-value');

      // Visual selection
      step.querySelectorAll('.sbg-quiz__card').forEach(function (c) {
        c.classList.remove('sbg-quiz__card--selected');
      });
      card.classList.add('sbg-quiz__card--selected');

      answers[stepNum] = value;

      // Auto-advance after short delay
      setTimeout(function () {
        if (stepNum < totalSteps) {
          currentStep = stepNum + 1;
          showStep(currentStep);
        } else {
          showResult();
        }
      }, 300);
    });

    // Retake
    retakeBtn.addEventListener('click', function () {
      answers = {};
      currentStep = 1;
      resultEl.classList.remove('sbg-quiz__result--visible');
      progressBar.style.display = '';
      // Clear all selections
      quiz.querySelectorAll('.sbg-quiz__card--selected').forEach(function (c) {
        c.classList.remove('sbg-quiz__card--selected');
      });
      showStep(1);
    });
  }

  // ==========================================
  // INIT
  // ==========================================
  function init() {
    var inits = [initHeroParallax, initScrollReveal, initStickyNav, initSmoothScroll, initAccordions, initSizeCalculator, initProductCarousels, initStyleQuiz];
    inits.forEach(function (fn) {
      try { fn(); } catch (e) { console.error('[SBG] Error in ' + fn.name + ':', e); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
